import { useApolloClient } from '@apollo/client';
import { Upload, type UploadProps, message } from 'antd';
import { flow } from 'lodash';
import { filter, isEmpty, isNil, map } from 'lodash/fp';
import { type MutableRefObject, useCallback, useMemo, useReducer, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import getApolloErrors from '../../../server/utils/getApolloErrors';
import { type SdmModuleSpecsFragment } from '../../api/fragments/SdmModuleSpecs';
import {
    type ImportSdmModuleSalesPersonCodeMappingsMutation,
    type ImportSdmModuleSalesPersonCodeMappingsMutationVariables,
    ImportSdmModuleSalesPersonCodeMappingsDocument,
} from '../../api/mutations/importSdmModuleSalesPersonCodeMappings';
import { CompanyTheme } from '../../api/types';
import { useThemeComponents } from '../../themes';
import { exportSdmSalesPersonCodeMappings } from '../../utilities/export';
import { allowedExtensions, validateExtensions } from '../../utilities/extensions';
import { useAccountContext } from '../contexts/AccountContextManager';
import {
    ErrorContent,
    InformationContent,
    UpdateDuplicateContent,
    defaultState,
    reducer,
    type DuplicateIds,
} from '../importExport';
import { UploadContainer } from '../importExport/ui';
import CancelButton from '../modal/CancelButton';
import OkButton from '../modal/OkButton';
import UploadContent from './UploadContent';

export type OnImportedHandler = (mappings: SdmModuleSpecsFragment['salesPersonCodeMappings']) => void;
type ExcelUploadModalProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    t: TFunction;
    moduleId: string;
    onImportedRef: MutableRefObject<OnImportedHandler | null>;
};

const ExcelUploadModal = ({ open, onClose, title, moduleId, t, onImportedRef }: ExcelUploadModalProps) => {
    const apolloClient = useApolloClient();
    const { token } = useAccountContext();
    const [state, dispatch] = useReducer(reducer, defaultState);
    const { file, errors, updateCount, duplicates } = state;
    const { theme, Modal } = useThemeComponents();

    const actions = useMemo(
        () => ({
            setUpdateCount: (updateCount: number | null) => dispatch({ type: 'setUpdateCount', updateCount }),
            setDuplicates: (duplicates: DuplicateIds[] | null) => dispatch({ type: 'setDuplicates', duplicates }),
            setErrors: (errors: string | null) => dispatch({ type: 'setErrors', errors }),
            setFile: (file: File | null) => dispatch({ type: 'setFile', file }),
            setDefault: () => dispatch({ type: 'setDefault' }),
        }),
        [dispatch]
    );

    const onCancelHandler = useCallback(() => {
        onClose();
        actions.setDefault();
    }, [onClose, actions]);

    const onUpload = useCallback(
        async inputFile => {
            try {
                actions.setFile(inputFile);
                const { data } = await apolloClient.mutate<
                    ImportSdmModuleSalesPersonCodeMappingsMutation,
                    ImportSdmModuleSalesPersonCodeMappingsMutationVariables
                >({
                    mutation: ImportSdmModuleSalesPersonCodeMappingsDocument,
                    variables: {
                        upload: inputFile,
                        duplicateIdsToBeUpdated: [],
                        isUpdate: false,
                        moduleId,
                    },
                });
                const { updateCount: count, duplicateIds } = data.response;

                if (duplicateIds.length > 0) {
                    const duplicated = duplicateIds.length > 1 ? ['Select all', ...duplicateIds] : duplicateIds;
                    actions.setDuplicates(duplicated.map(id => ({ label: id, key: id, value: false })));
                } else {
                    actions.setUpdateCount(count);
                    const { current } = data.response;
                    onImportedRef.current?.(current);
                }
            } catch (error) {
                const apolloErrors = getApolloErrors(error);

                if (apolloErrors?.$root) {
                    actions.setErrors(apolloErrors?.$root);
                }
            }
        },
        [actions, apolloClient, moduleId, onImportedRef]
    );

    const onDuplicateClick = useCallback(
        item => {
            if (item.key === 'Select all') {
                const updatedDuplicates = duplicates.map(duplicate => ({ ...duplicate, value: !item.value }));

                actions.setDuplicates(updatedDuplicates);
            } else {
                const updatedDuplicates = duplicates.map(duplicate => {
                    if (duplicate.key === item.key) {
                        return { ...duplicate, value: !item.value };
                    }

                    return duplicate;
                });

                actions.setDuplicates(updatedDuplicates);
            }
        },
        [duplicates, actions]
    );

    const onOverrideClick = useCallback(async () => {
        try {
            const duplicateIdsToBeUpdated = flow([
                filter((x: DuplicateIds) => x.value && x.key !== 'Select all'),
                map((x: DuplicateIds) => x.key),
            ])(duplicates);

            const { data } = await apolloClient.mutate<
                ImportSdmModuleSalesPersonCodeMappingsMutation,
                ImportSdmModuleSalesPersonCodeMappingsMutationVariables
            >({
                mutation: ImportSdmModuleSalesPersonCodeMappingsDocument,
                variables: {
                    upload: file,
                    duplicateIdsToBeUpdated,
                    isUpdate: true,
                    moduleId,
                },
            });

            const { updateCount: count, current } = data.response;

            actions.setUpdateCount(count);

            onImportedRef.current?.(current);
        } catch (error) {
            const apolloErrors = getApolloErrors(error);

            if (apolloErrors?.$root) {
                actions.setErrors(apolloErrors?.$root);
            }
        }
    }, [apolloClient, file, duplicates, actions, moduleId, onImportedRef]);

    const handleOnChange = useCallback(
        ({ file }) => {
            if (file instanceof File) {
                onUpload(file);
            }
        },
        [onUpload]
    );
    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        file => {
            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [t]
    );
    const props: UploadProps = useMemo(
        () => ({
            accept: allowedExtensions.excel.join(','),
            showUploadList: false,
            onChange: handleOnChange,
            beforeUpload,
        }),
        [beforeUpload, handleOnChange]
    );

    const content = useMemo(() => {
        const onDownloadClick = () => {
            exportSdmSalesPersonCodeMappings(token);
        };

        if (!isNil(errors)) {
            return <ErrorContent errors={errors} />;
        }

        if (!isNil(updateCount)) {
            return (
                <InformationContent
                    type={t('importExportExcel:import.sdmSalesPersonCodeMappings.type')}
                    updateCount={updateCount}
                />
            );
        }

        if (!isEmpty(duplicates)) {
            return <UpdateDuplicateContent duplicates={duplicates} onDuplicateClick={onDuplicateClick} />;
        }

        return <UploadContent onDownloadClick={onDownloadClick} />;
    }, [errors, updateCount, duplicates, t, token, onDuplicateClick]);
    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="cancel" onClick={onCancelHandler}>
                {t('importExportExcel:actions.cancel')}
            </CancelButton>
        );

        const okButton = !isEmpty(duplicates) ? (
            <OkButton key="submit" onClick={onOverrideClick}>
                {t('importExportExcel:actions.confirmOverride')}
            </OkButton>
        ) : (
            <UploadContainer {...props}>
                <OkButton key="submit">{t('importExportExcel:actions.uploadFile')}</OkButton>
            </UploadContainer>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [duplicates, onOverrideClick, onCancelHandler, props, theme, t]);

    return (
        <Modal footer={footer} onCancel={onCancelHandler} open={open} title={title} width={550}>
            {content}
        </Modal>
    );
};

export default ExcelUploadModal;

export const useExcelUploadModal = (moduleId: string, onImportedRef: MutableRefObject<OnImportedHandler | null>) => {
    const { t } = useTranslation('importExportExcel');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const actions = useMemo(() => {
        const close = () => setIsModalOpen(false);

        return {
            open: () => {
                setIsModalOpen(true);
            },
            close,
        };
    }, [setIsModalOpen]);

    return {
        ...actions,
        render: () => (
            <ExcelUploadModal
                moduleId={moduleId}
                onClose={actions.close}
                onImportedRef={onImportedRef}
                open={isModalOpen}
                t={t}
                title={t('importExportExcel:import.sdmSalesPersonCodeMappings.title')}
            />
        ),
    };
};
